import * as SentryReact from '@sentry/react';
import React from 'react';

import { WebCampaignType } from '@/global';
import { useAccount } from '@/hooks/useAccount';
import { useAppModals } from '@/hooks/useAppModals';
import { amplitudeModule } from '@/modules/amplitude';
import api from '@/modules/api';
import { AxonModule } from '@/modules/axon';
import { fireConfetti } from '@/modules/confetti';
import { FacebookModule } from '@/modules/facebook';
import { GoogleAnalyticsModule } from '@/modules/googleAnalytics';
import { queryParamsModule } from '@/modules/queryParams';
import {
  SubscriptionDataSchema,
  SubscriptionDataType,
} from '@/modules/schemas';
import { webCampaign } from '@/modules/webCampaign';
import { userService } from '@/services/user';
import { createAxonPurchasePayload } from '@/utils/axonUtils';
import { canLogEvents } from '@/utils/canLogEvents';
import { getFbCookie } from '@/utils/getFbCookie';
import { getGaCookie } from '@/utils/getGaCookie';
import {
  clearPurchasedPlan,
  getPurchasedPlan,
} from '@/utils/purchasedPlanStorage';

import DefaultVariant from './Variants/Default/Default';
import DefaultUpsaleVariant from './Variants/DefaultUpsale/Default';
import DevmodeVariant from './Variants/Devmode/Devmode';
import DominateVariant from './Variants/Domination/Domination';
import MaximVariant from './Variants/Maxim/Maxim';
import MultigenderVariant from './Variants/Multigender/Multigender';
import MultigenderAioVariant from './Variants/MultigenderAio/MultigenderAio';
import MultigenderOnlyWebVariant from './Variants/MultigenderOnlyWeb/MultigenderOnlyWeb';
import MultigenderWebVariant from './Variants/MultigenderWeb/MultigenderWeb';
import OnboardingBasedVariant from './Variants/OnboardingBased/OnboardingBased';
import OneNightStandVariant from './Variants/OneNightStand/OneNightStand';
import OneNightStandConstructorVariant from './Variants/OneNightStandConstructor/OneNightStandConstructor';
import OneNightStandManVariant from './Variants/OneNightStandMan/OneNightStandMan';
import PaymentlessVariant from './Variants/Paymentless/Paymentless';
import RoleplayVariant from './Variants/Roleplay/Roleplay';
import TransVariant from './Variants/Trans';
import WebVersionVariant from './Variants/WebVersion/WebVersion';
import WithEmailVariant from './Variants/WithEmail/WithEmail';
import WithStepsVariant from './Variants/WithSteps/WithSteps';
import WithStepsWebVariant from './Variants/WithStepsWeb/WithStepsWeb';

const Page: Record<WebCampaignType['successPage'], React.FC> = {
  devmode: DevmodeVariant,
  default: DefaultVariant,
  'default-upsale': DefaultUpsaleVariant,
  'onboarding-based': OnboardingBasedVariant,
  'one-night-stand': OneNightStandVariant,
  'one-night-stand-constructor': OneNightStandConstructorVariant,
  'one-night-stand-man': OneNightStandManVariant,
  multigender: MultigenderVariant,
  'multigender-web': MultigenderWebVariant,
  'multigender-only-web': MultigenderOnlyWebVariant,
  'multigender-aio': MultigenderAioVariant,
  'with-email': WithEmailVariant,
  paymentless: PaymentlessVariant,
  domination: DominateVariant,
  roleplay: RoleplayVariant,
  trans: TransVariant,
  'web-version': WebVersionVariant,
  maxim: MaximVariant,
  'with-steps': WithStepsVariant,
  'with-steps-web': WithStepsWebVariant,
};

const SuccessPaymentPage: React.FC = () => {
  const { user } = useAccount();
  const { hide, isVisible } = useAppModals('paywall');
  const userId = user?.id;
  const purchasedPlan = getPurchasedPlan();

  React.useEffect(() => {
    if (isVisible) {
      hide();
    }
  }, [isVisible, hide]);

  React.useEffect(() => {
    queryParamsModule.removeParam('step');
    fireConfetti();

    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Succesful Payment',
      });
    }

    if (queryParamsModule.getParam('ga') !== '1') {
      queryParamsModule.setParam('ga', '1');

      const { fbc, fbp } = getFbCookie();
      const { gaUserPseudoId } = getGaCookie();

      userService.createOrUpdate({
        ...(fbc && { fbc }),
        ...(fbp && { fbp }),
        ...(gaUserPseudoId && { ga_user_pseudo_id: gaUserPseudoId }),
      });

      AxonModule.trackPageView(userId);
      if (canLogEvents()) {
        FacebookModule.trackEvent('AddToCart');
        api
          .get<SubscriptionDataType>('v1', '/me/subscription_data')
          .then((res) => {
            const subcriptionDataParse = SubscriptionDataSchema.safeParse(res);

            if (subcriptionDataParse.success) {
              const { data } = subcriptionDataParse;
              GoogleAnalyticsModule.trackEvent('cw_subscribe', data);
              GoogleAnalyticsModule.trackEvent('purchase', {
                transaction_id: data.transaction_id,
                value: data.price,
                currency: data.currency,
              });

              if (purchasedPlan) {
                const payload = createAxonPurchasePayload(
                  purchasedPlan,
                  data.transaction_id,
                  data.price,
                  data.currency,
                );
                AxonModule.pushEvent(
                  'purchase',
                  'axon_purchase',
                  payload,
                  userId,
                );
                clearPurchasedPlan();
              }
            } else {
              const validationError = subcriptionDataParse.error;
              SentryReact.captureException(
                new Error('Subscription data validation failed'),
                {
                  tags: {
                    validation_error: true,
                    endpoint: '/me/subscription_data',
                  },
                  extra: {
                    validationErrors: validationError.errors,
                    receivedData: res,
                    userId,
                  },
                },
              );
            }
          })
          .catch((err) => {
            console.error(err);
          });
      }
    } else {
      queryParamsModule.setParam('ga', '1');
    }
  }, []);

  const SuccessPage = Page[webCampaign.setup.successPage];

  return <SuccessPage />;
};

export default SuccessPaymentPage;
